<script lang="ts" setup>
import { ref, onMounted, computed, onBeforeMount, inject, watch } from 'vue';
import axiosInstance from '@/plugins/axios';
import { useRoute, useRouter } from 'vue-router';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { useSessionStore } from '@/store/sessionStore';

interface Question {
  id: string;
  translations: {
    [key: string]: {
      title: string;
      questionText: string;
      lawText: string;
      questionHelp: string;
    };
  };
  options?: string[];
}

const sessionStore = useSessionStore();
const user = computed(() => sessionStore.getUser());
const gF = inject('gF') as any;
const route = useRoute();
const router = useRouter();
const targetTranslation = ref<any>({});
const rootTranslation = ref<any>({});
const allQuestions = ref<Question[]>([]);
const currentTranslation = ref<string>(route.params.translation || 'de');
const missingFields = ref<Record<string, string[]>>({});
const showOnlyMissingFields = ref(false);

const getTranslation = async (targetTranslationId) => {
  try {
    gF.startLoading();
    const res = await axiosInstance.get(`/translations/${targetTranslationId}`);
    gF.endLoading();
    return res.data;
  } catch (error) {
    console.log(error);
    gF.endLoading();
  }
};

const getAllQuestions = async () => {
  try {
    const res = await axiosInstance.get(`/questions`);
    allQuestions.value = res.data;
  } catch (error) {
    console.log(error);
  }
};

const fetchProgress = async () => {
  try {
    const res = await axiosInstance.get(`/translations/${route.params.translation}`);
    missingFields.value = res.data.progress?.missingFields || {};
  } catch (error) {
    console.error('Error fetching translation progress:', error);
  }
};
const getProgressColor = (prefix: string) => {
  const missing = Object.entries(missingFields.value)
    .filter(([key]) => key.startsWith(prefix))
    .flatMap(([, fields]) => fields);

  if (missing.length === 0) return 'btn-success';

  const isRequiredFieldMissing = missing.some((field) => {
    const fullKey = `${prefix}.${field}`;
    const missingInRequired = missingFields.value[prefix]?.includes(field);
    return missingInRequired && !['questionHelp', 'lawText', 'info'].includes(field);
  });

  const isOptionalTreatedAsRequired = missing.some((field) => {
    const missingInRequired = missingFields.value[prefix]?.includes(field);
    return missingInRequired && ['questionHelp', 'lawText', 'info'].includes(field);
  });

  return isRequiredFieldMissing || isOptionalTreatedAsRequired ? 'btn-danger' : 'btn-warning';
};


const hasMissingFields = (prefix: string) => {
  return Object.keys(missingFields.value).some(key => key.startsWith(prefix));
};

const getQuestionIdentifier = (questionID) => {
  const question = allQuestions.value.find((q) => q._id === questionID);
  return question ? question.identifier : 'none';
};

const selectedCategory = ref<string>((route.query.category as string) || 'all');
const searchQuery = ref('');

const filteredQuestions = computed(() => {
  let questions = allQuestions.value;

  if (selectedCategory.value === 'all') {
    questions = questions.filter((q) => q.identifier && q.identifier.length === 4 && !/^9\d{3}$/.test(q.identifier));
  } else if (selectedCategory.value === 'outdated') {
    questions = questions.filter((q) => !q.identifier || q.identifier.length !== 4);
  } else {
    const categoryMapping = {
      '1xxx': /^1\d{3}$/,
      '2xxx': /^2\d{3}$/,
      '3xxx': /^3\d{3}$/,
      '41xx': /^41\d{2}$/,
      '42xx': /^42\d{2}$/,
      '5xxx': /^5\d{3}$/,
      '6xxx': /^6\d{3}$/,
      '7xxx': /^7\d{3}$/,
      '9xxx': /^9\d{3}$/,
    };
    const regex = categoryMapping[selectedCategory.value];
    if (regex) {
      questions = questions.filter((q) => q.identifier && q.identifier.length === 4 && regex.test(q.identifier));
    }
  }

  if (searchQuery.value) {
    const query = searchQuery.value.toLowerCase();
    questions = questions.filter(
      (q) =>
        q.translations[currentTranslation.value]?.title?.toLowerCase().includes(query) ||
        q.translations[currentTranslation.value]?.questionText?.toLowerCase().includes(query)
    );
  }

  if (showOnlyMissingFields.value) {
    questions = questions.filter((q) => {
      const hasQuestionIssues = hasMissingFields(`questions.${q._id}`);
      const hasOptionIssues = (q.options || []).some(id => hasMissingFields(`options.${id}`));
      return hasQuestionIssues || hasOptionIssues;
    });
  }

  return questions;
});

watch([selectedCategory, showOnlyMissingFields], async () => {
  await fetchProgress();
});

const goTo = (questionID: string, type: string) => {
  router.push(`/tenant/translations/${route.params.translation}/questions_options/${type}/${questionID}`);
};

const getOptionsProgressColor = (optionIds: string[] = []) => {
  const missing = optionIds.flatMap((id) => {
    return missingFields.value[`options.${id}`] || [];
  });

  if (missing.length === 0) return 'btn-success';

  const isRequiredFieldMissing = missing.some((field, _, arr) => {
    return !['questionHelp', 'lawText', 'info'].includes(field);
  });

  const isOptionalTreatedAsRequired = missing.some((field) =>
    ['questionHelp', 'lawText', 'info'].includes(field)
  );

  return isRequiredFieldMissing || isOptionalTreatedAsRequired ? 'btn-danger' : 'btn-warning';
};


onBeforeMount(async () => {
  if (!route.query.category) {
    router.replace({ query: { ...route.query, category: 'all' } });
  }

  selectedCategory.value = (route.query.category as string) || 'all';

  targetTranslation.value = await getTranslation(route.params.translation);
  if (route.params.translation === '670e1e32d4634dd3c3ec7478') {
    rootTranslation.value = await getTranslation('670fad078be48456eb53b250');
  } else {
    rootTranslation.value = await getTranslation('670e1e32d4634dd3c3ec7478');
  }

  await getAllQuestions();
  await fetchProgress();
});
</script>



<template>
  <div>
    <div class="flex justify-content-between align-items-center mb-4">
      <div class="col-6 m-0 p-0">
        <select v-model="selectedCategory" class="form-select m-0">
          <option value="all">All Categories</option>
          <option value="1xxx">Classification of AI System</option>
          <option value="2xxx">Role</option>
          <option value="3xxx">Prohibited Systems</option>
          <option value="41xx">High-Risk AI</option>
          <option value="42xx">High-Risk AI III</option>
          <option value="5xxx">Transparency Obligations</option>
          <option value="6xxx">GPAI</option>
          <option value="7xxx">Excluded Systems</option>
          <option v-if="user?.role.startsWith('ai_lab')" value="9xxx">Miscellaneous</option>
          <option v-if="user?.role.startsWith('ai_lab')" value="outdated">Outdated</option>
        </select>
      </div>
      <div class="col-3 text-end m-0 p-0">
        <ToggleButton v-model="showOnlyMissingFields" onLabel="Show only Missing Fields" offLabel="Show all Fields"
          @change="fetchProgress" />
      </div>
    </div>
<div class="d-flex flex-wrap gap-3">
  <div
    v-for="question in filteredQuestions"
    :key="question.id"
    class="card-wrapper"
  >
    <Card class="p-3 w-100 h-100 card">
  <template #title>
    <div class="text-muted fs-6">
      <Tag severity="secondary">{{ getQuestionIdentifier(question._id) }}</Tag>
      {{
        targetTranslation.questions?.[question._id]?.title ||
        rootTranslation.questions?.[question._id]?.title ||
        question.title
      }}
    </div>
    <div>
      {{
        targetTranslation.questions?.[question._id]?.questionText ||
        rootTranslation.questions?.[question._id]?.questionText ||
        question.questionText
      }}
    </div>
  </template>

  <template #subtitle>
    <div class="translation-progress">
      <div class="d-flex justify-content-between align-items-center mb-2">
        <Button
          class="w-100 d-flex justify-content-between align-items-center"
          :class="'btn ' + getProgressColor(`questions.${question._id}`)"
          @click="goTo(question._id, 'question')"
        >
          <span>Question</span>
          <FontAwesomeIcon icon="fa-solid fa-arrow-right" />
        </Button>
      </div>
      <div class="d-flex justify-content-between align-items-center">
        <Button
          class="w-100 d-flex justify-content-between align-items-center"
          :class="'btn ' + getOptionsProgressColor(question.options)"
          @click="goTo(question._id, 'options')"
        >
          <span>Options</span>
          <FontAwesomeIcon icon="fa-solid fa-arrow-right" />
        </Button>
      </div>
    </div>
  </template>
</Card>

    </div>
    </div>
  </div>
</template>

<style scoped>
.btn-danger {
  background-color: #dc3545 !important;
  border-color: unset;
  color: white;
}

.btn-warning {
  background-color: #ffc107 !important;
  border-color: unset;

  color: black;
}

.btn-success {
  background-color: #198754 !important;
  border-color: unset;

  color: white;
}

.p-card {
  box-shadow: none;
  border-radius: var(--bs-card-border-radius);
  border: var(--bs-card-border-width) solid var(--bs-card-border-color);
}

.card-wrapper {
  flex: 1 1 calc(50% - 1rem); /* zwei nebeneinander mit Abstand */
  min-width: 300px; /* optional */
}
</style>
